import { Box, Link, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { getBatchInfo } from "@/modules/lead-page/utils";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

interface ItemData {
  title?: string;
  link?: string;
}

interface ExpandableItemsListProps {
  items: ItemData[];
}

const ExpandableItemsList = ({ items }: ExpandableItemsListProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const [visibleBatchCount, setVisibleBatchCount] = useState(1);
  const BATCH_SIZE = isMobile ? 1 : 2;

  const handleShowAll = () => {
    // Calculate total batches needed to show all items
    const totalBatches = Math.ceil(items.length / BATCH_SIZE);
    setVisibleBatchCount(totalBatches);
  };

  return (
    <Stack spacing={theme.nawy.spacing.xxs}>
      {Array.from({ length: visibleBatchCount }).map((_, batchIndex) => {
        const { batchLinks, isLastBatch, remainingCount } = getBatchInfo(
          items,
          batchIndex,
          visibleBatchCount,
          BATCH_SIZE
        );

        return (
          <Box
            key={batchIndex}
            sx={{
              display: "flex",
              gap: theme.nawy.spacing.sm,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {batchLinks.map((item, index) => {
              // If item has a link, render as Link component
              if (item.link) {
                return (
                  <Link
                    key={item.link}
                    href={item.link}
                    target="_blank"
                    sx={{
                      ...theme.nawy.typography.english.variants.body.md.regular,
                      color: theme.nawy.colors.semantic.text.accent,
                    }}
                  >
                    {item.title}
                  </Link>
                );
              }
              // Otherwise, render as plain text (Typography)
              return (
                <Typography
                  key={index}
                  sx={{
                    ...theme.nawy.typography.english.variants.body.md.regular,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {item.title}
                </Typography>
              );
            })}
            {isLastBatch && remainingCount > 0 && (
              <Typography
                onClick={handleShowAll}
                sx={{
                  cursor: "pointer",
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.nawy.colors.semantic.text.accent,
                }}
              >
                +{remainingCount}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default ExpandableItemsList;
